import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { EPSESite } from '@/types/epse';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface EPSESiteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  site?: EPSESite;
  onSave: (data: Partial<EPSESite>) => void;
}

export function EPSESiteModal({ open, onOpenChange, site, onSave }: EPSESiteModalProps) {
  const [formData, setFormData] = useState({
    name: site?.name || '',
    org_id: site?.org_id || '',
    connection_kw_limit: site?.connection_kw_limit || 100,
    annual_kwh_total: site?.annual_kwh_total || null,
    pv_installed_kwp: site?.pv_installed_kwp || null,
    tags: site?.tags?.join(', ') || '',
  });
  
  const { data: organizations } = useQuery({
    queryKey: ['organizations-select'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .is('deleted_at', null)
        .order('name');
      
      if (error) throw error;
      return data;
    },
  });
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    onSave({
      name: formData.name,
      org_id: formData.org_id || null,
      connection_kw_limit: formData.connection_kw_limit,
      annual_kwh_total: formData.annual_kwh_total,
      pv_installed_kwp: formData.pv_installed_kwp,
      tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
    });
    
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{site ? 'Site Bewerken' : 'Nieuwe Site'}</DialogTitle>
            <DialogDescription>
              Vul de basisgegevens in voor de energieanalyse
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Sitenaam *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Bijv. Distributiecentrum Utrecht"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="org">Organisatie</Label>
              <Select
                value={formData.org_id}
                onValueChange={(value) => setFormData({ ...formData, org_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer organisatie" />
                </SelectTrigger>
                <SelectContent>
                  {organizations?.map(org => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="limit">Contractlimiet (kW) *</Label>
                <Input
                  id="limit"
                  type="number"
                  value={formData.connection_kw_limit}
                  onChange={(e) => setFormData({ ...formData, connection_kw_limit: Number(e.target.value) })}
                  min={0}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="annual">Jaarverbruik (kWh)</Label>
                <Input
                  id="annual"
                  type="number"
                  value={formData.annual_kwh_total || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    annual_kwh_total: e.target.value ? Number(e.target.value) : null 
                  })}
                  min={0}
                  placeholder="Optioneel"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pv">PV Vermogen (kWp)</Label>
              <Input
                id="pv"
                type="number"
                value={formData.pv_installed_kwp || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  pv_installed_kwp: e.target.value ? Number(e.target.value) : null 
                })}
                min={0}
                placeholder="Laat leeg indien geen PV"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tags">Tags</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="koeling, laadinfra, ploegendienst (komma-gescheiden)"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button type="submit">
              {site ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
